import { useEffect, useState } from 'react'

import classes from './Conversation.module.scss'

export type ThinkingIndicatorProps = Readonly<{
  /** Rotating status copy; advances on a timer and holds on the last line. */
  messages: string[]
  /** The single stable line announced to assistive tech (rotation is `aria-hidden`). */
  workingLabel: string
  rotateMs?: number
  testId?: string
}>

/**
 * Bouncing dots plus a rotating status label that fades in on each change. The
 * rotating label is `aria-hidden` — it sits inside the conversation's `role="log"`
 * region, so every rotation would otherwise be announced as new content; a single
 * stable, visually-hidden `workingLabel` is announced instead.
 */
export function ThinkingIndicator({
  messages,
  workingLabel,
  rotateMs = 800,
  testId,
}: ThinkingIndicatorProps) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (messages.length <= 1) return undefined

    const timer = setInterval(
      () => setIndex((prev) => Math.min(prev + 1, messages.length - 1)),
      rotateMs
    )

    return () => clearInterval(timer)
  }, [messages.length, rotateMs])

  return (
    <div className={classes.thinking} data-testid={testId}>
      <span className={classes.dots} aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span className={classes.visuallyHidden}>{workingLabel}</span>
      <span key={index} className={classes.thinkingLabel} aria-hidden="true">
        {messages[index]}
      </span>
    </div>
  )
}
